// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { CountNounSchema, PackNounSchema, type CountNoun, type PackNoun, type ShoppingListItem } from "@bitebalance/contracts"
import { unitLabel } from "@/lib/units"

/**
 * The Shop row's summary (approved mock 2026-08-31), shared by the Shop page and its journey
 * tests: what to BUY — as packs, in words — and the NEED it was rounded up from, or "Pantry
 * covers it" when the confirmed stock already does. Pure (no React Native) so the shared
 * journey fixtures can pin it.
 *
 * Shop list clarity (2026-10-06). The row used to show only packs × pack size ("Zwiebel 9×",
 * "Knoblauch 10×") and hide the need: a 1 kg net of onions IS nine pieces and a bulb of garlic IS
 * ten cloves, but a bare "9×" hid both facts and looked like far too much. Now a count row says what
 * is counted and, when a pack holds several, what the pack is: "1 Netz (9 Stück)", "2 Knollen
 * (20 Zehen)", "1 Packung (6 Stück)", "3 Stück" (a pack of one is just the items). The words come
 * from the catalog's curated `countNoun` / `packNoun` (packages/i18n holds the German/English
 * forms); a row without them (an older server, an uncurated ingredient) keeps today's "N×".
 * No weight is derived or hard-coded: production prices can override a pack's basis, so "1 kg"
 * would sometimes be false.
 *
 * KEEP IN SYNC with mobile/src/lib/shopRowView.ts (no shared home exists for the two
 * clients' view-models; both are tested against the same cases).
 */
export type TFn = (key: string, vars?: Record<string, string | number>) => string

/**
 * Defence in depth: the contract parse drops a noun a NEWER server adds to the closed sets, and data that bypasses the
 * parse (harness, fixtures, direct callers) must still never reach the screen as a raw key: such a noun is treated as ABSENT.
 */
const isCountNoun = (value: unknown): value is CountNoun => typeof value === "string" && (CountNounSchema.options as readonly string[]).includes(value)
const isPackNoun = (value: unknown): value is PackNoun => typeof value === "string" && (PackNounSchema.options as readonly string[]).includes(value)

/** The row's count noun, or null when it is absent or unknown to this client (the row then keeps the plain "N×"). */
export function countNounOf(item: Pick<ShoppingListItem, "countNoun">): CountNoun | null {
  return isCountNoun(item.countNoun) ? item.countNoun : null
}

/**
 * The row's pack noun, or null when it is absent or unknown. A pack can only be worded when its units can: without a valid count
 * noun there is no pack noun. An unknown pack noun beside a valid count noun drops ONLY the pack wrapper ("18 Stück", not "2 ? (18 Stück)"):
 * what is counted is still known and true, the pack's name is not.
 */
export function packNounOf(item: Pick<ShoppingListItem, "countNoun" | "packNoun">): PackNoun | null {
  return countNounOf(item) !== null && isPackNoun(item.packNoun) ? item.packNoun : null
}

/** One decimal at most, no trailing ".0" — the value exactly as the Shop prints it. */
export function trimNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "")
}

/**
 * A number as the reader's language writes it: the SAME digits (the existing rounding is untouched — no Intl.NumberFormat, which rounds
 * x.x5 ties differently and groups thousands) with the decimal mark swapped through ONE i18n key (`views.shop.decimalMark`: "," in
 * German, "." in English, so English output is byte-identical). Takes the translate function every helper here already has, so no
 * signature changed.
 */
export function localNum(value: number | string, t: TFn): string {
  return String(value).replace(".", t("views.shop.decimalMark"))
}

/** `trimNum` in the reader's decimal mark. */
export function numText(n: number, t: TFn): string {
  return localNum(trimNum(n), t)
}

/** The value as it will be PRINTED: 0.96 and 1.04 both print "1", so both are singular. */
const shownValue = (n: number): number => Number(trimNum(n))

/**
 * One counted unit of a count row: singular when the PRINTED number is exactly 1 ("1 Zehe"; also 0.96 and 1.04, which print "1"),
 * plural otherwise ("10 Zehen"; 0.5 is plural). Null for a noun this client does not know.
 */
export function countWord(noun: string, n: number, t: TFn): string | null {
  if (!isCountNoun(noun)) return null
  return t(`views.shop.countNoun.${noun}${shownValue(n) === 1 ? "One" : "Many"}`)
}

/** A pack of a count row ("1 Knolle", "2 Knollen"). Null for a noun this client does not know. */
export function packWord(noun: string, n: number, t: TFn): string | null {
  if (!isPackNoun(noun)) return null
  return t(`views.shop.packNoun.${noun}${shownValue(n) === 1 ? "One" : "Many"}`)
}

/**
 * What this row buys, in up to three forms: `quantity` is the plain total ("1 kg", "500 g", "18 Stück", "9×"); `text` adds the pack when a
 * count row's pack holds several units ("2 Netze (18 Stück)"); `packOnly` is that pack without its bracketed total ("2 Netze"), set only
 * for a pack-wrapped row. `rowNeed` compares the need with `quantity`, never with the raw numbers.
 */
function boughtOf(item: ShoppingListItem, t: TFn): { quantity: string; text: string; packOnly?: string } | null {
  if (item.manual || item.buyQuantity === null || !item.selectedPack) return null
  const packs = item.buyQuantity
  const total = packs * item.selectedPack.quantity
  const unit = item.selectedPack.unit
  const plain = (quantity: string) => ({ quantity, text: quantity })
  if (unit === "g" && total >= 1000) return plain(`${numText(total / 1000, t)} kg`)
  if (unit === "ml" && total >= 1000) return plain(`${numText(total / 1000, t)} l`)
  if (unit === "count") {
    const noun = countNounOf(item)
    const unitWord = noun ? countWord(noun, total, t) : null
    if (!unitWord) return plain(`${numText(total, t)}×`)
    const items = `${numText(total, t)} ${unitWord}`
    // A pack that holds exactly one unit is the unit itself (apple, banana, a real per-piece price): no wrapper.
    const packNoun = packNounOf(item)
    const packName = packNoun ? packWord(packNoun, packs, t) : null
    if (packName && item.selectedPack.quantity > 1) {
      const pack = `${numText(packs, t)} ${packName}`
      return { quantity: items, text: `${pack} (${items})`, packOnly: pack }
    }
    return plain(items)
  }
  return plain(`${numText(total, t)} ${unit}`)
}

/**
 * buy 1 × 1000 g reads as "1 kg"; g→kg and ml→l collapse at 1000. A count row reads as words when
 * the catalog curated them (see the file comment) and as "N×" otherwise.
 * Returns null for manual rows (their own quantity renders instead).
 */
export function buyAmountText(item: ShoppingListItem, t: TFn): string | null {
  return boughtOf(item, t)?.text ?? null
}

/** Rounding slack: a need of 12.0000001 (float noise) shows 12, not 13. */
const NEED_EPSILON = 1e-6

/**
 * The need of a COUNT row as it is printed (founder 2026-10-07): rounded UP to a whole number — you cannot buy 0.3 of a clove — with a
 * minimum of 1, then CAPPED at the count bought. The cap is the safety valve for the calculator's tolerance (it may serve a need of
 * 9.04 with ONE pack of 9, `shopping.ts` `EPSILON` step): a plain ceil would print "Bedarf 10 Stück" beside "9 Stück kaufen", a need
 * larger than what is bought. With the cap it prints 9, which equals the amount bought, so the hide-when-equal rule hides it. Without a
 * resolvable count pack (no `selectedPack` in count) there is nothing to cap against and the rounded number stands.
 */
function displayedCountNeed(item: ShoppingListItem, amount: number): number {
  const rounded = Math.max(1, Math.ceil(amount - NEED_EPSILON))
  const pack = item.selectedPack
  if (item.buyQuantity === null || !pack || pack.unit !== "count") return rounded
  return Math.min(rounded, item.buyQuantity * pack.quantity)
}

/** A need amount: whole grams / millilitres (a decimal gram is noise), kg / l from 1000 (one decimal), whole numbers ROUNDED UP for counted units, never "0". */
function needAmount(item: ShoppingListItem, amount: number, unit: string, t: TFn): string | null {
  if (unit === "g" || unit === "ml") {
    const whole = Math.max(1, Math.round(amount))
    if (whole >= 1000) return `${numText(whole / 1000, t)} ${unit === "g" ? "kg" : "l"}`
    return `${whole} ${unit}`
  }
  if (unit === "count") {
    // Without a (known) count noun the row keeps today's wording and shows no need (we could only say "7×").
    const noun = countNounOf(item)
    if (!noun) return null
    const shown = displayedCountNeed(item, amount)
    const word = countWord(noun, shown, t)
    return word ? `${numText(shown, t)} ${word}` : null
  }
  return `${numText(amount, t)} ${unit}`
}

/**
 * The NEED this row's pack was rounded up from, from fields already on the wire: `netNeeded`
 * (what the plan needs AFTER the confirmed kitchen stock — the quantity the pack rounding applies
 * to). With nothing reserved it reads "Bedarf 760 g"; when the kitchen covers part of the plan's
 * requirement it reads "Noch 560 g nötig" — the same number, worded so it never claims to be the
 * plan's whole requirement. Null for manual rows, covered rows, rows without a usable amount — and rows whose need
 * prints exactly the amount bought (see below).
 */
export function rowNeed(item: ShoppingListItem, t: TFn): string | null {
  if (item.manual || pantryCovered(item) || !item.netNeeded) return null
  const { amount, unit } = item.netNeeded.canonical
  if (!(amount > 0)) return null
  const need = needAmount(item, amount, unit, t)
  if (!need) return null
  // Founder decision 2026-10-06: a need that prints exactly what is bought says nothing the buy line does not — it is hidden.
  // The comparison is on the DISPLAYED text (same quantity, same unit), never the raw numbers: 999.6 g prints "1 kg" and a 1 kg pack
  // buys "1 kg" → hidden; 996 g prints "996 g" → shown; "1 Packung (6 Stück)" with a need of 5 stays, with a need of 6 it is hidden.
  if (need === boughtOf(item, t)?.quantity) return null
  const kitchenCoversPart = (item.pantryReserved?.canonical.amount ?? 0) > 0
  return t(kitchenCoversPart ? "views.shop.needLineRemaining" : "views.shop.needLine", { need })
}

/** A calculated row the confirmed stock fully covers (nothing to buy). */
export function pantryCovered(item: ShoppingListItem): boolean {
  return !item.manual && item.buyQuantity === 0
}

/**
 * Founder decision 2026-10-07: when a need is shown beside a pack-wrapped count row, the bracketed total is dropped so the line fits on
 * one line — "2 Knollen kaufen · Bedarf 11 Zehen" (the bracket stays when no need is shown: "1 Knolle (10 Zehen) kaufen"). It applies to
 * EVERY pack-wrapped count row (Knoblauch, Zwiebel, Ei, Tortilla). This is the ONE place that says so: to restrict it to garlic, change
 * the body to `return countNounOf(item) === "clove"`.
 */
function dropsBracketWhenNeedShown(_item: ShoppingListItem): boolean {
  return true
}

/** The subtitle under the row name: covered → "Pantry covers it"; to buy → "Buy …"; manual → its quantity; else nothing. */
export function rowSubtitle(item: ShoppingListItem, t: TFn): string | null {
  if (pantryCovered(item)) return t("views.shop.pantryCovers")
  const bought = boughtOf(item, t)
  if (bought) {
    const qty = bought.packOnly && dropsBracketWhenNeedShown(item) && rowNeed(item, t) !== null ? bought.packOnly : bought.text
    return t("views.shop.buyLine", { qty })
  }
  // canonical units never reach the user raw: a manual "count" row reads "1 Stk.", not "1 count"
  if (item.manual && item.requiredQuantity) return `${localNum(item.requiredQuantity.display.quantity, t)} ${unitLabel(item.requiredQuantity.display.unit, t)}`
  return null
}

/**
 * A calculated row's name as the Shop prints it. The catalog keeps `names.en` lower-case for use inside sentences (Amy's included), so
 * the ENGLISH list upper-cases the first character ONLY — "rolled oats" → "Rolled oats", the rest untouched. German nouns are already
 * capitalised and stay as served; a manual row is user-typed text and is NEVER altered. Presentation only: sorting is case-insensitive
 * (`localeCompare` with base sensitivity) and keeps using the served name.
 */
export function shopRowName(item: Pick<ShoppingListItem, "displayName" | "manual">, locale: string): string {
  if (item.manual || locale !== "en") return item.displayName
  return item.displayName.charAt(0).toUpperCase() + item.displayName.slice(1)
}
